import { ForwardIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { publicQuotes } from 'rails_routes'
import { Banner, Button, Modal, TextButton } from 'slab'
import { cn } from 'utils'
import {
  useDeleteQuotingQuoteResultsQuoteIdRevokeRecipientAccess as useRevokeRecipientAccess,
  useGetQuotingQuoteResultsQuoteIdAccess as getQuoteAccess,
  usePatchQuotingQuoteResultsQuoteIdUpdateRecipientPermissions as useUpdateRecipientPermissions,
  usePostQuotingQuoteResultsQuoteIdInvite as useShareQuote,
  usePostResourceAccessTokensIdCopy as useCopyLink,
} from 'utils/web_api'
import {
  type ResourceAccessTokensResourceAccessTokenAbilitiesItem,
  type ResourceAccessTokensResourceAccessTokenRecipient as ResourceAccessTokenRecipient,
  ResourceAccessTokensResourceAccessTokenStatus as ResourceAccessTokenStatus,
} from 'utils/web_api.schemas'

import { EditPermissionsView } from './EditPermissionsView'
import { RecipientsList } from './RecipientsList'
import { ShareQuoteForm } from './ShareQuoteForm'

export type StagedInvite = {
  email: string,
  abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]
}

type ShareQuoteButtonModalProps = {
  quoteId: string,
  buttonVariant: 'primary' | 'secondary',
  prospectName: string,
  hasUnsavedChanges: boolean,
  onSaveChanges: () => void,
  onDiscardChanges: () => void,
  isSavingChanges: boolean,
  canSendEditorInvites: boolean,
  canSendOnboardInvites: boolean,
  canEditQuote: boolean
}

export const ShareQuoteButtonModal = ({
  quoteId,
  buttonVariant = 'secondary',
  prospectName,
  hasUnsavedChanges,
  onSaveChanges,
  onDiscardChanges,
  isSavingChanges,
  canSendEditorInvites,
  canSendOnboardInvites,
  canEditQuote,
}: ShareQuoteButtonModalProps) => {
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false)
  const [successMessageDetails, setSuccessMessageDetails] = useState<{ fromEmail: string, toEmail: string } | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [shakeSaveChangesBanner] = useState(false)
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null)
  const [copyingLinkForEmail, setCopyingLinkForEmail] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState<'share' | 'recipients' | 'editPermissions'>('share')
  const [stagedInvites, setStagedInvites] = useState<StagedInvite[]>([])
  const [editingRecipientId, setEditingRecipientId] = useState<string | null>(null)
  const [editingAbilities, setEditingAbilities] = useState<ResourceAccessTokensResourceAccessTokenAbilitiesItem[]>([])
  const [revokingRecipientId, setRevokingRecipientId] = useState<string | null>(null)

  const shareQuoteMutation = useShareQuote()
  const copyLinkMutation = useCopyLink()
  const updatePermissionsMutation = useUpdateRecipientPermissions()
  const revokeAccessMutation = useRevokeRecipientAccess()

  const {
    data: quoteAccessData,
    isPending: isQuoteAccessPending,
    refetch: refetchQuoteAccess
  } = getQuoteAccess(quoteId)

  const invitedRecipients = quoteAccessData?.token_recipients
  const partnerAdminRelationships = quoteAccessData?.partner_admin_relationships

  const updateQuoteRecipients = (invites: StagedInvite[]) => {
    if (invites.length === 0) return
    shareQuoteMutation.mutate(
      { quoteId, data: { recipients: invites } },
      {
        onSuccess: (data) => {
          setSuccessMessageDetails({
            fromEmail: data.from_email!,
            toEmail: data
              .partner_admin_relationships.map(p => p.partner_admin.email)
              .concat(data.token_recipients.map(r => r.email))
              .join(', ')
          })
          refetchQuoteAccess()
          setStagedInvites([])
          setInvitingEmail(null)
        },
        onError: () => {
          setErrorMessage('Error sharing quote, please try again or contact support.')
          setInvitingEmail(null)
        }
      }
    )
  }

  const handleCopyLink = (tokenId: string | null, recipient: ResourceAccessTokenRecipient) => {
    setCopyingLinkForEmail(recipient.email)

    if (!tokenId) {
      setCopyingLinkForEmail(null)
      toast.error('Failed to copy link, please try resending invite email.')
      return
    }
    copyLinkMutation.mutate(
      { id: tokenId },
      {
        onSuccess: (accessToken) => {
          const returnedTokenId = accessToken.id
          if (!returnedTokenId) {
            setCopyingLinkForEmail(null)
            toast.error('Failed to get token ID')
            return
          }
          const path = publicQuotes.show({ id: quoteId, query: { token: returnedTokenId } })
          const url = `${window.location.origin}${path}`
          navigator.clipboard.writeText(url).then(() => {
            setCopyingLinkForEmail(null)
            setShareModalIsOpen(false)
            toast.success('Link copied to clipboard')
          }).catch(() => {
            setCopyingLinkForEmail(null)
            setShareModalIsOpen(false)
            toast.error('Failed to copy link to clipboard')
          })
        },
        onError: () => {
          setCopyingLinkForEmail(null)
          toast.error('Failed to generate link, please try again')
        }
      }
    )
  }

  const handleEditPermissions = (recipientId: string, abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => {
    setEditingRecipientId(recipientId)
    // Only include abilities the user can modify in the editable state
    const editableAbilities = abilities.filter(a => {
      if (a === 'view') return true
      if (a === 'edit') return canSendEditorInvites
      if (a === 'onboard') return canSendOnboardInvites
      return false
    })
    setEditingAbilities(editableAbilities)
    setCurrentTab('editPermissions')
  }

  // Get the abilities that the recipient has but the user cannot modify
  const getUnmodifiableAbilities = (recipientAbilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => {
    return recipientAbilities.filter(a => {
      if (a === 'edit' && !canSendEditorInvites) return true
      if (a === 'onboard' && !canSendOnboardInvites) return true
      return false
    })
  }

  const handleSavePermissions = () => {
    if (!editingRecipientId) return

    const unmodifiableAbilities = getUnmodifiableAbilities(editingRecipientOriginalAbilities)
    const allAbilities = [...new Set([...editingAbilities, ...unmodifiableAbilities])]

    updatePermissionsMutation.mutate(
      { quoteId, data: { recipient_id: editingRecipientId, abilities: allAbilities } as { recipient_id: string } },
      {
        onSuccess: () => {
          refetchQuoteAccess()
          setCurrentTab('recipients')
          setEditingRecipientId(null)
          setEditingAbilities([])
          toast.success('Permissions updated successfully')
        },
        onError: () => {
          toast.error('Failed to update permissions')
        }
      }
    )
  }

  const handleCancelEditPermissions = () => {
    setCurrentTab('recipients')
    setEditingRecipientId(null)
    setEditingAbilities([])
  }

  const toggleAbility = (ability: ResourceAccessTokensResourceAccessTokenAbilitiesItem) => {
    if (ability === 'view') return
    if (ability === 'edit' && !canSendEditorInvites) return
    if (ability === 'onboard' && !canSendOnboardInvites) return

    setEditingAbilities(prev => {
      if (prev.includes(ability)) {
        return prev.filter(a => a !== ability)
      } else {
        return [...prev, ability]
      }
    })
  }

  const handleRevokeAccess = (recipientId: string) => {
    setRevokingRecipientId(recipientId)
    revokeAccessMutation.mutate(
      { quoteId, params: { recipient_id: recipientId } },
      {
        onSuccess: () => {
          refetchQuoteAccess()
          setRevokingRecipientId(null)
          toast.success('Access revoked successfully')
        },
        onError: () => {
          setRevokingRecipientId(null)
          toast.error('Failed to revoke access')
        }
      }
    )
  }

  const handleResendInvite = (email: string, abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => {
    setInvitingEmail(email)
    updateQuoteRecipients([{ email, abilities }])
  }

  const editingRecipient = invitedRecipients?.find(r => r.id === editingRecipientId)

  const editingRecipientOriginalAbilities = editingRecipient
    ? Array.from(
      new Set(
        editingRecipient.tokens
          .filter(token => token.status === ResourceAccessTokenStatus.redeemed || token.status === ResourceAccessTokenStatus.unredeemed)
          .flatMap(token => token.abilities)
      )
    )
    : []

  const closeModal = () => {
    setShareModalIsOpen(false)
    setTimeout(() => {
      setStagedInvites([])
      setSuccessMessageDetails(null)
      setErrorMessage(null)
      setEditingRecipientId(null)
      setEditingAbilities([])
      setCurrentTab('share')
    }, 500)
  }

  useEffect(() => {
    const commandEnterHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && e.metaKey && shareModalIsOpen && currentTab === 'share') {
        updateQuoteRecipients(stagedInvites)
      }
    }
    if (shareModalIsOpen) {
      document.addEventListener('keydown', commandEnterHandler)
    } else {
      document.removeEventListener('keydown', commandEnterHandler)
    }
    return () => document.removeEventListener('keydown', commandEnterHandler)
  }, [shareModalIsOpen, currentTab, updateQuoteRecipients, stagedInvites])

  return (
    <>
      <Modal open={shareModalIsOpen} onOpenChange={closeModal} width='medium'>
        <Modal.Body>
          <Modal.Title>Share allowance strategy</Modal.Title>
          {currentTab !== 'editPermissions' && (
            <div className='flex items-center gap-2 border-b border-gray-200 mb-2'>
              <div
                className={cn(
                  'px-4 py-2 cursor-pointer',
                  { 'border-b-2 border-gray-700': currentTab === 'share' },
                  { 'hover:border-b border-gray-500': currentTab !== 'share' }
                )}
                onClick={() => setCurrentTab('share')}
              >
                Invite others
              </div>
              <div
                className={cn(
                  'px-4 py-2 cursor-pointer',
                  { 'border-b-2 border-gray-700': currentTab === 'recipients' },
                  { 'hover:border-b border-gray-500': currentTab !== 'recipients' }
                )}
                onClick={() => setCurrentTab('recipients')}
              >
                People with access
              </div>
            </div>
          )}
          <div>
            {currentTab === 'share' && (
              <div className='space-y-2'>
                {successMessageDetails && (
                  <Banner type='success' title='Quote shared' onDismiss={() => setSuccessMessageDetails(null)}>
                    <p className='text-sm text-green-500'>
                      A link to view the quote has been sent to <span className='font-semibold italic'>{successMessageDetails.toEmail}</span>.
                    </p>
                    <p className='text-sm text-green-500'>
                      They should look for an email from <span className='font-semibold italic'>{successMessageDetails.fromEmail}</span> with the subject line <span className='font-bold italic'>"Review your new Thatch quote for {prospectName}!"</span>.
                    </p>
                  </Banner>
                )}
                {errorMessage && (
                  <Banner type='critical' title={errorMessage} onDismiss={() => setErrorMessage(null)} />
                )}
                {hasUnsavedChanges && (
                  <div className={cn('transition-transform origin-top duration-500', shakeSaveChangesBanner ? 'animate-shake' : '')}>
                    <Banner
                      type='caution'
                      title='Unsaved changes'
                      cta={
                        <div className='space-x-4'>
                          <Button variant='secondary' onClick={onSaveChanges} disabled={isSavingChanges}>
                            Save changes
                          </Button>
                          <TextButton
                            color='gray'
                            onClick={onDiscardChanges}
                          >
                            Discard changes
                          </TextButton>
                        </div>
                      }
                    >
                      <p className='text-sm text-gray-500'>
                        The quote has unsaved changes, you must save or discard them before sharing.
                      </p>
                    </Banner>
                  </div>
                )}
                <ShareQuoteForm
                  onCancelClick={closeModal}
                  canSendEditorInvites={canSendEditorInvites}
                  canSendOnboardInvites={canSendOnboardInvites}
                  stagedInvites={stagedInvites}
                  onStagedInvitesChanged={setStagedInvites}
                  onSendStagedInvites={() => updateQuoteRecipients(stagedInvites)}
                  isSendingInvites={shareQuoteMutation.isPending}
                />
              </div>
            )}
            {currentTab === 'recipients' && (
              <RecipientsList
                recipients={invitedRecipients}
                partnerAdminRelationships={partnerAdminRelationships}
                isLoading={isQuoteAccessPending}
                invitingEmail={invitingEmail}
                copyingLinkForEmail={copyingLinkForEmail}
                revokingRecipientId={revokingRecipientId}
                onResendInvite={handleResendInvite}
                onCopyLink={handleCopyLink}
                onEditPermissions={handleEditPermissions}
                onRevokeAccess={handleRevokeAccess}
                canEditQuote={canEditQuote}
                canSendEditorInvites={canSendEditorInvites}
                canSendOnboardInvites={canSendOnboardInvites}
              />
            )}
            {currentTab === 'editPermissions' && editingRecipient && (
              <EditPermissionsView
                recipient={editingRecipient}
                editingAbilities={editingAbilities}
                originalAbilities={editingRecipientOriginalAbilities}
                canSendEditorInvites={canSendEditorInvites}
                canSendOnboardInvites={canSendOnboardInvites}
                isSaving={updatePermissionsMutation.isPending}
                onToggleAbility={toggleAbility}
                onSave={handleSavePermissions}
                onCancel={handleCancelEditPermissions}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Button
        variant={buttonVariant}
        onClick={() => setShareModalIsOpen(true)}
        disabled={shareQuoteMutation.isPending}
        leftIcon={ForwardIcon}
      >
        Share quote
      </Button>
    </>
  )
}
